import { Button, Spinner } from '@heroui/react';
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { StripeAddressValue, StripeCheckoutProps } from '../types/payment';

// Component for when we don't have a clientSecret yet - show loading while auto-init runs, or manual button if needed
const StripePaymentInitiator: React.FC<StripeCheckoutProps> = ({
  onInitiatePayment,
  disabled = false,
  canInitiatePayment = false,
  isInitializing = false,
}) => {
  if (isInitializing) {
    return (
      <div className='flex flex-col items-center justify-center py-8 space-y-4'>
        <Spinner size='lg' />
        <p className='text-gray-600'>Preparing your checkout...</p>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <Button
        color='primary'
        size='lg'
        className='w-full'
        disabled={disabled || !canInitiatePayment}
        onClick={onInitiatePayment}
      >
        Continue to payment
      </Button>
    </div>
  );
};

// Component for when we have a clientSecret and are inside Elements provider
const StripePaymentForm: React.FC<StripeCheckoutProps> = ({
  amount,
  paymentIntentData: _paymentIntentData,
  onSuccess,
  onError,
  onInitiatePayment: _onInitiatePayment,
  onAddressChange,
  disabled = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { formatCurrency, convertPrice } = useLocale();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const formatAmount = (value: number) => formatCurrency(convertPrice(value));

  useEffect(() => {
    setIsReady(true);
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError('Stripe has not loaded yet');
      return;
    }

    await handlePayment();
  };

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      // Get the entered address from Address Element BEFORE confirming - required to stamp on order
      const addressElement = elements!.getElement('address');
      if (!addressElement) {
        onError('Address form is not ready. Please refresh and try again.');
        setIsProcessing(false);
        return;
      }

      const { complete, value } = await addressElement.getValue();
      if (!complete || !value) {
        onError('Please complete your delivery address before payment.');
        setIsProcessing(false);
        return;
      }

      const enteredAddress: StripeAddressValue = {
        name: value.name,
        address: {
          line1: value.address.line1,
          line2: value.address.line2 ?? undefined,
          city: value.address.city,
          state: value.address.state,
          postal_code: value.address.postal_code,
          country: value.address.country,
        },
        phone: value.phone,
      };

      // Address Element auto-passes shipping when confirming; no need to pass billing_details manually
      const result = await stripe!.confirmPayment({
        elements: elements!,
        confirmParams: {
          return_url: `${window.location.origin}/checkout?success=true`,
        },
        redirect: 'if_required',
      });

      if (result.error) {
        onError(result.error.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        setIsProcessing(false);
        onSuccess(enteredAddress);
      }
    } catch (err: any) {
      onError(err.message || 'An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className='space-y-6'
      aria-label='Payment Form'
    >
      <div className='space-y-4'>
        <div>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>
            Delivery address
          </h3>
          <AddressElement
            options={{
              mode: 'shipping',
              allowedCountries: ['GB'],
              fields: {
                phone: 'always',
              },
            }}
            onChange={
              onAddressChange
                ? ev =>
                    onAddressChange(
                      ev.complete && ev.value
                        ? {
                            name: ev.value.name,
                            address: {
                              line1: ev.value.address.line1,
                              line2: ev.value.address.line2 ?? undefined,
                              city: ev.value.address.city,
                              state: ev.value.address.state,
                              postal_code: ev.value.address.postal_code,
                              country: ev.value.address.country,
                            },
                            phone: ev.value.phone,
                          }
                        : null,
                      ev.complete
                    )
                : undefined
            }
          />
        </div>
        <div>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>
            Payment Details
          </h3>
          <PaymentElement
            options={{
              layout: 'tabs',
              business: {
                name: 'CodedGhost Minis',
              },
              wallets: {
                link: 'never',
              },
            }}
          />
        </div>
      </div>

      <div className='border-t pt-4'>
        <div className='flex items-center justify-between mb-4'>
          <span className='text-lg font-semibold'>Total Amount:</span>
          <span className='text-2xl font-bold text-blue-600'>
            {formatAmount(amount)}
          </span>
        </div>

        <Button
          type='submit'
          color='primary'
          size='lg'
          className='w-full'
          disabled={
            !stripe || !elements || !isReady || isProcessing || disabled
          }
          isLoading={isProcessing}
        >
          {isProcessing ? 'Processing...' : `Pay ${formatAmount(amount)}`}
        </Button>

        <div className='mt-4 text-center'>
          <p className='text-sm text-gray-500'>
            Your payment is secured by Stripe. We never see your card details.
          </p>
        </div>
      </div>
    </form>
  );
};

// Main component that decides which component to render
const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  amount,
  paymentIntentData,
  onSuccess,
  onError,
  onInitiatePayment,
  onAddressChange,
  disabled = false,
  canInitiatePayment = false,
  isInitializing = false,
  stripePromise,
}) => {
  // If no clientSecret, show the payment initiator (no Elements wrapper - uses no Stripe form components)
  if (!paymentIntentData?.clientSecret) {
    if (!stripePromise) {
      return (
        <div className='text-center py-8'>
          <p className='text-red-600'>
            Stripe is not available. Please try another payment method.
          </p>
        </div>
      );
    }

    return (
      <StripePaymentInitiator
        amount={amount}
        paymentIntentData={paymentIntentData}
        onSuccess={onSuccess}
        onError={onError}
        onInitiatePayment={onInitiatePayment}
        onAddressChange={onAddressChange}
        disabled={disabled}
        canInitiatePayment={canInitiatePayment}
        isInitializing={isInitializing}
      />
    );
  }

  // If we have clientSecret and stripePromise, wrap in Elements provider
  if (stripePromise) {
    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret: paymentIntentData.clientSecret,
          locale: 'en-GB', // UK-only for MVP: localise Stripe UI to UK
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#3b82f6',
            },
          },
        }}
      >
        <StripePaymentForm
          amount={amount}
          paymentIntentData={paymentIntentData}
          onSuccess={onSuccess}
          onError={onError}
          onInitiatePayment={onInitiatePayment}
          onAddressChange={onAddressChange}
          disabled={disabled}
        />
      </Elements>
    );
  }

  // Fallback if no stripePromise
  return (
    <div className='text-center py-8'>
      <p className='text-red-600'>
        Stripe is not available. Please try another payment method.
      </p>
    </div>
  );
};

export default StripeCheckout;
